import React, { useState, useEffect, useCallback } from 'react';
import { ShoppingBag, Menu as MenuIcon, X, User, LogOut, Heart } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useRestaurantData } from '../hooks/useRestaurantData';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import AuthModal from './AuthModal';
import UserDropdown from './UserDropdown';
import FavoritesDrawer from './FavoritesDrawer';
import { useFavorites } from '../context/FavoritesContext';
import './Navbar.css';

const Navbar = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isOpenNow, setIsOpenNow] = useState(false);
    const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

    const location = useLocation();
    const data = useRestaurantData();
    const { cartCount, setIsCartOpen } = useCart();
    const { user, logout } = useAuth();
    const { favorites, setIsFavoritesOpen } = useFavorites();
    const favCount = favorites.length;

    // Optimized Scroll Handler
    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 20) {
                if (!isScrolled) setIsScrolled(true);
            } else {
                if (isScrolled) setIsScrolled(false);
            }
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [isScrolled]);

    // Robust Opening Status Check
    const checkStatus = useCallback(() => {
        if (!data?.restaurant?.hours) return;

        const now = new Date();
        const dayName = now.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
        const currentTime = now.getHours() * 100 + now.getMinutes();

        const dayHours = data.restaurant.hours[dayName];
        if (dayHours && dayHours.open && dayHours.close) {
            const openTime = parseInt(dayHours.open.replace(':', ''));
            const closeTime = parseInt(dayHours.close.replace(':', ''));

            // Handle cases where close time is after midnight (e.g., 02:00)
            if (closeTime < openTime) {
                setIsOpenNow(currentTime >= openTime || currentTime <= closeTime);
            } else {
                setIsOpenNow(currentTime >= openTime && currentTime <= closeTime);
            }
        }
    }, [data]);

    useEffect(() => {
        checkStatus();
        const interval = setInterval(checkStatus, 30000); // Check every 30s
        return () => clearInterval(interval);
    }, [checkStatus]);

    const navLinks = [
        { name: 'Notre Carte', path: '/' },
        { name: 'Localisation', path: '/contact' },
    ];

    const isDarkText = isScrolled || location.pathname !== '/';

    return (
        <nav className={`navbar ${isScrolled ? 'navbar-scrolled' : ''}`}>
            <div className="nav-container">
                {/* Logo & Status */}
                <div className="nav-logo-group">
                    <Link to="/" className="nav-logo" onClick={() => setIsMenuOpen(false)}>
                        <div className="logo-icon">SH</div>
                        <span className={`logo-text ${isDarkText ? 'text-dark' : 'text-white'} md:block hidden`}>
                            SUGAR HUT
                        </span>
                    </Link>

                    <div className={`status-badge ${isOpenNow ? 'status-open' : 'status-closed'}`}>
                        <span className={`status-dot ${isOpenNow ? 'dot-online' : 'dot-offline'}`} />
                        {isOpenNow ? 'Ouvert' : 'Fermé'}
                    </div>
                </div>

                {/* Desktop Links */}
                <div className="nav-menu">
                    {navLinks.map((link) => (
                        <Link
                            key={link.name}
                            to={link.path}
                            className={`nav-link ${isDarkText ? 'text-dark' : 'text-white'}`}
                        >
                            {link.name}
                        </Link>
                    ))}
                </div>

                {/* Actions */}
                <div className="nav-actions">
                    <button
                        onClick={() => setIsFavoritesOpen(true)}
                        className="action-btn btn-fav"
                        aria-label="Voir les favoris"
                    >
                        <Heart size={18} fill={favCount > 0 ? "currentColor" : "none"} />
                        <AnimatePresence>
                            {favCount > 0 && (
                                <motion.span
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    exit={{ scale: 0 }}
                                    className="cart-badge fav-badge"
                                >
                                    {favCount}
                                </motion.span>
                            )}
                        </AnimatePresence>
                    </button>

                    <button
                        onClick={() => setIsCartOpen(true)}
                        className="action-btn btn-cart"
                        aria-label="Voir le panier"
                    >
                        <ShoppingBag size={18} />
                        <AnimatePresence>
                            {cartCount > 0 && (
                                <motion.span
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    exit={{ scale: 0 }}
                                    className="cart-badge"
                                >
                                    {cartCount}
                                </motion.span>
                            )}
                        </AnimatePresence>
                    </button>

                    {user ? (
                        <UserDropdown isDarkText={isDarkText} />
                    ) : (
                        <button
                            onClick={() => setIsAuthModalOpen(true)}
                            className="action-btn btn-auth"
                            aria-label="Connexion"
                        >
                            <User size={18} />
                        </button>
                    )}

                    <button
                        className="mobile-toggle"
                        onClick={() => setIsMenuOpen(true)}
                        aria-label="Menu"
                    >
                        <MenuIcon size={24} className={isDarkText ? 'text-dark' : 'text-white'} />
                    </button>
                </div>
            </div>

            <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} />
            <FavoritesDrawer />

            {/* Mobile Menu Overlay */}
            <AnimatePresence>
                {isMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0, x: '100%' }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: '100%' }}
                        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                        className="mobile-nav-overlay"
                    >
                        <div className="mobile-nav-header">
                            <div className="nav-logo">
                                <div className="logo-icon">SH</div>
                                <span className="logo-text text-white">SUGAR HUT</span>
                            </div>
                            <button
                                onClick={() => setIsMenuOpen(false)}
                                className="text-white p-2"
                            >
                                <X size={32} />
                            </button>
                        </div>

                        <div className="mobile-nav-links">
                            {user && (
                                <div className="mobile-user-info">
                                    <div className="user-avatar">{user.name.charAt(0)}</div>
                                    <div className="flex flex-col">
                                        <span className="text-white font-bold">{user.name}</span>
                                        <span className="text-white/40 text-xs">{user.points} points fidélité</span>
                                    </div>
                                </div>
                            )}

                            {navLinks.map((link) => (
                                <Link
                                    key={link.name}
                                    to={link.path}
                                    onClick={() => setIsMenuOpen(false)}
                                    className="mobile-nav-link"
                                >
                                    {link.name}
                                </Link>
                            ))}

                            {user ? (
                                <>
                                    {(user.role === 'admin' || user.email === 'superbotboys@gmail.com') && (
                                        <Link
                                            to="/admin"
                                            onClick={() => setIsMenuOpen(false)}
                                            className="mobile-nav-link"
                                            style={{ color: 'var(--primary)' }}
                                        >
                                            Administration
                                        </Link>
                                    )}
                                    <Link
                                        to="/account"
                                        onClick={() => setIsMenuOpen(false)}
                                        className="mobile-nav-link"
                                    >
                                        Mon Compte
                                    </Link>
                                    <button
                                        onClick={() => {
                                            logout();
                                            setIsMenuOpen(false);
                                        }}
                                        className="mobile-nav-link text-red-500 text-left flex items-center gap-2"
                                    >
                                        <LogOut size={18} /> Déconnexion
                                    </button>
                                </>
                            ) : (
                                <button
                                    onClick={() => {
                                        setIsMenuOpen(false);
                                        setIsAuthModalOpen(true);
                                    }}
                                    className="mobile-nav-link text-left"
                                    style={{ color: 'var(--primary)' }}
                                >
                                    Connexion / Inscription
                                </button>
                            )}
                        </div>

                        <div className="mt-auto pt-10 border-t border-white/10 text-white/40 text-sm">
                            65 bis Avenue Paul Doumer, Montesson<br />
                            01 23 45 67 89
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </nav>
    );
};

export default Navbar;
